import { get } from 'lodash-es';

import { ParamType } from '@/components/paramsAbout';

export function jsonSafeParse<T = any>(
  str: string | null | undefined,
  fallbackVal: T = undefined as any,
): T | undefined {
  try {
    return JSON.parse(str!);
  } catch (e) {
    console.warn(e);
    return fallbackVal;
  }
}

export function jsonSafeStringify<T = any>(
  json: any,
  fallbackVal = '',
): string {
  try {
    return JSON.stringify(json);
  } catch (e) {
    console.warn(e);
    return fallbackVal;
  }
}

export const toLowerCaseFirstLetter = (str: string) =>
  str.charAt(0).toLowerCase() + str.slice(1);

export function getErrorMsg(val: any) {
  if (!val || typeof val === 'string') return val;
  return val.message || val.msg || val.status_msg;
}

export function isNullLike(val: any): val is null | undefined {
  return val === null || val === undefined;
}

export function isLegalNum(val: any) {
  return !isNaN(val) && (val || val === 0);
}

export function noop(...args: any[]) {}

export function id(val: any) {
  return val;
}

export function calcVal<T extends any, Args extends any[]>(
  val: T | ((...args: Args) => T),
  ...$args: Args
) {
  if (typeof val === 'function')
    return (val as (...args: any[]) => T)(...$args);
  return val;
}

/**
 * Parse version string to major.minor format
 * @param version - Version string like "1.2.3"
 * @returns Major.minor version like "1.2" or original version if invalid
 */
export function toMajorMinor(version: string | null | undefined): string {
  if (!version) return '';
  const parts = version.split('.');
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}` : version;
}

/**
 * Parse version string to major.minor.0 format
 * @param version - Version string like "1.2.3"
 * @returns Major.minor.0 version like "1.2.0" or undefined if invalid
 */
export function toMajorMinorZero(version: string | null | undefined): string | undefined {
  if (!version) return undefined;
  const parts = version.split('.');
  return parts.length >= 2 ? `${parts[0]}.${parts[1]}.0` : version;
}

export function changeURLArg(url: string, arg: string, arg_val: string) {
  const pattern = arg + '=([^&]*)';
  const replaceText = arg + '=' + arg_val;
  if (url.match(pattern)) {
    // Use RegExp constructor instead of eval for security
    const regex = new RegExp(`(${arg}=)([^&]*)`, 'gi');
    return url.replace(regex, replaceText);
  } else {
    if (url.match('[?]')) {
      return url + '&' + replaceText;
    } else {
      return url + '?' + replaceText;
    }
  }
}
export function getNearestItemIndex<T>(
  list: T[],
  referNum: number,
  options?: {
    useGte?: boolean;
    useLte?: boolean;
    calcCompareNum?(item: T): number;
  },
) {
  if (!list?.length) return { sortList: [], index: -1 };
  const calc = options?.calcCompareNum || Number;
  const $list = [...list].sort((a, b) => calc(a) - calc(b));

  // 二分法
  // 变量：leftCursor - 左边的索引，rightCursor - 右边的索引
  // 比较左右索引的中间元素与 referNum：
  //    如果中间元素比 referNum 大，则将 rightCursor 修改为中间元素的索引;
  //    如果中间元素比 referNum 小，则将 leftCursor 修改为中间元素的索引;
  //    如果中间元素与 referNum 相等，直接返回;
  // 直到 leftCursor 与 rightCursor 之间没有中间元素了
  const index = (() => {
    let leftCursor = 0;
    let rightCursor = $list.length - 1;
    while (rightCursor - leftCursor > 1) {
      const middle = Math.floor((rightCursor + leftCursor) / 2);
      const offset = referNum - calc($list[middle]);
      if (offset === 0) return middle;
      else if (offset < 0) rightCursor = middle;
      else leftCursor = middle;
    }
    const leftOffset = referNum - calc($list[leftCursor]);
    const rightOffset = referNum - calc($list[rightCursor]);
    if (leftOffset === 0) return leftCursor;
    if (leftOffset < 0) return options?.useLte ? -1 : leftCursor;
    if (rightOffset === 0) return rightCursor;
    if (rightOffset > 0) return options?.useGte ? rightCursor + 1 : rightCursor;
    if (options?.useGte) return rightCursor;
    if (options?.useLte) return leftCursor;
    return Math.abs(leftOffset) <= Math.abs(rightOffset)
      ? leftCursor
      : rightCursor;
  })();
  return { sortList: $list, index };
}

export function getNearestItem<T>(
  list: T[],
  referNum: number,
  options?: {
    useGte?: boolean;
    useLte?: boolean;
    calcCompareNum?(item: T): number;
  },
): T | undefined {
  const { sortList, index } = getNearestItemIndex(list, referNum, options);
  return sortList[index];
}

export function calcMinAndMax(
  data: any[],
  valueKey?: string,
  /**
   * @default { margin: [0.2, 0.2], integer: true }
   * */
  options?: {
    min?: number;
    max?: number;
    margin?: number | [number, number];
    integer?: boolean;
  },
) {
  let $min = Infinity;
  let $max = -Infinity;
  data.forEach(it => {
    const val = valueKey ? +get(it, valueKey) : it;
    $min = Math.min($min, val);
    $max = Math.max($max, val);
  });
  const margin = isNullLike(options?.margin)
    ? [0.2, 0.2]
    : typeof options!.margin === 'number'
      ? [options!.margin, options!.margin]
      : options!.margin;
  const integer = options?.integer ?? true;
  const cvt = (v: number) => (integer ? Math.floor(v) : v);
  return {
    min: Math.max(
      options?.min ?? -Infinity,
      cvt($min - ($max - $min) * margin[0]),
    ),
    max: Math.min(
      options?.max ?? Infinity,
      cvt($max + ($max - $min) * margin[1]),
    ),
  };
}

export function toQueryString(obj: Record<string, any>) {
  return Object.entries(obj)
    .map(
      ([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`,
    )
    .join('&');
}

export const findKey = (type?: ParamType): string => {
  if (!type) {
    return 'key';
  }
  const keys: Record<string, string> = {
    [ParamType.ExpiryValue]: 'expiry',
    [ParamType.InstrumentValue]: 'instrument',
    [ParamType.TextNumberList]: 'key',
    [ParamType.TextPercentageList]: 'key',
  };
  return keys[type] || 'key';
};

export const isValidJSON = (str: string) => {
  if (!str || !str.trim()) return false;
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
}
